import React, { useState } from "react";
import { ThemeProvider } from "../components/ThemeProvider";
import InformationCard from "../components/shared/InformationCard";
import { CategoryTabs, FaqItem } from "../components";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowLeft,
  faQuestionCircle,
} from "@fortawesome/free-solid-svg-icons";
import {
  HelpCircle,
  Zap,
  Clock,
  BarChart,
  Target,
  DollarSign,
  Shield,
} from "lucide-react";
import ContinuousBackground from "../components/landing/ContinuousBackground";
import { Header, Footer } from "./home/index";
import { Link } from "react-router-dom";

// Original categories and FAQ data from original implementation
const categories = [
  { label: "All", value: "all" },
  { label: "Basics", value: "basics" },
  { label: "Strategy", value: "strategy" },
  { label: "Brokerage", value: "brokerage" },
];

// FAQ Items
const allFaqItems =
  [
    {
      "question": "What is marketpulse.exchange?",
      "answer": "marketpulse.exchange is a smart trading platform that helps intraday traders identify high-momentum stocks in the NSE FnO segment. It combines real-time data, intelligent scanners, and one-click execution to simplify profitable trading.",
      "category": "basics"
    },
    {
      "question": "Who is this platform built for?",
      "answer": "If you're an intraday trader looking for low-risk, high-reward trades - especially in options or futures - marketpulse.exchange is for you. Whether you're a beginner or a seasoned pro, you'll get an edge using our momentum tools.",
      "category": "basics"
    },
    {
      "question": "How is marketpulse.exchange different from other scanners?",
      "answer": "Most scanners show you 'what's happening' - we show you where the smart money is flowing. With predictive tools like the Pi Scanner, Sector Pulse, and real-time trade setups in the Strategy Console, we help you act early, not react late.",
      "category": "basics"
    },
    {
      "question": "What is the Pi Scanner?",
      "answer": "Pi (Predictive Intelligence) is our real-time momentum scanner. It tracks how frequently a stock appears in smart money scans and ranks them based on hit frequency + sector strength. High Pi Score = higher probability of momentum continuation.",
      "category": "strategy"
    },
    {
      "question": "What do the Pi stock labels mean? (Good to Enter, Watchlist, Ignore)",
      "answer": "- Good to Enter: Strong real-time signals - ideal for intraday entries\n- Watchlist: Stocks showing signs but not strong enough yet\n- Ignore: Not worth acting on right now based on momentum and sector filters",
      "category": "strategy"
    },
    {
      "question": "What is the best time to take entries?",
      "answer": "Based on our internal analysis, most strong intraday moves begin between 10:15 AM and 11:15 AM. Focus on high Pi Score stocks during this window for the best risk-reward.",
      "category": "strategy"
    },
    {
      "question": "What are Strike Strategies (like 5 MIN/10 MIN Momentum Strike)?",
      "answer": "These are pre-defined, back-tested strategies that track short-term bursts of momentum. Just pick a direction (Bullish/Bearish) and see the stocks that fit that setup in real-time.",
      "category": "strategy"
    },
    {
      "question": "Can I place trades directly from marketpulse.exchange?",
      "answer": "Yes! We've integrated with brokers like Dhan and AlgoTest to support one-click trade execution right from your dashboard. AlgoTest helps you to 100% automate your Entry, Exit, Stop Loss, Trailing SL, Target and more - completely removing emotions from your trades.",
      "category": "brokerage"
    },
    {
      "question": "How do I connect my broker or AlgoTest account?",
      "answer": "Just head to the 'Learn' section inside the platform to watch step-by-step videos on connecting your broker and using AlgoTest automation. It's quick, simple, and fully guided.",
      "category": "brokerage"
    },
    {
      "question": "Do I need a trading account to use the platform?",
      "answer": "You can explore scanners and momentum tools without linking a broker. But to place trades directly or automate using AlgoTest, you'll need to connect a supported trading account.",
      "category": "brokerage"
    },
    {
      "question": "How often is the Pi Scanner updated?",
      "answer": "The Pi Scanner updates every minute during live market hours, so you're always acting on fresh, real-time momentum data.",
      "category": "strategy"
    },
    {
      "question": "What does a high Pi Score really mean?",
      "answer": "A high Pi Score indicates that a stock is consistently appearing in momentum scans and is likely gaining attention from smart money. It's not just price action - it's behavioral confirmation across time and sector flow.",
      "category": "strategy"
    },
    {
      "question": "How many stocks should I track at once?",
      "answer": "We recommend focusing on 3 to 4 high Pi Score stocks during your entry zone (around 10:15-11:15 AM). Quality over quantity keeps your decision-making clean and precise.",
      "category": "strategy"
    },
    {
      "question": "Can I use marketpulse.exchange on mobile?",
      "answer": "Yes! The platform is fully responsive. You can track momentum, execute trades, and follow strategies from your phone or tablet with ease.",
      "category": "basics"
    },
    {
      "question": "Will you add more brokers in the future?",
      "answer": "Yes - we're actively working on integrating with more brokers to make execution seamless for every trader, regardless of where they trade.",
      "category": "brokerage"
    },
    {
      "question": "How do I know if a trade idea is still active?",
      "answer": "Look at both the Pi Score and Sector Pulse. If a stock's Pi Score is dropping or the sector is weakening, it may be losing steam. We recommend setting personal exit triggers or using AlgoTest automation to manage trades dynamically.",
      "category": "strategy"
    },
    {
      "question": "Can I request a new feature or strategy?",
      "answer": "Absolutely. We love user feedback. You can submit feature requests via the Support chat or directly inside the platform. Many of our current features came from power users just like you.",
      "category": "basics"
    }
  ]

// Original FaqPage component exactly as it is - for dashboard use
export function FaqPage() {
  const [filteredFaqItems, setFilteredFaqItems] = useState(allFaqItems);

  const handleFilterChange = (category) => {
    if (category === "all") {
      setFilteredFaqItems(allFaqItems);
    } else {
      setFilteredFaqItems(
        allFaqItems.filter((item) => item.category === category)
      );
    }
  };

  return (
    <div className="flex flex-col grow shrink pb-10 max-md:max-w-full px-6 bg-darkMode-background transition min-h-screen justify-center">
      {/* Header Section (remains the same) */}
      <div className="flex flex-col w-full max-w-[1160px] max-md:max-w-full px-4 md:px-6 lg:px-8">
        <div className="flex gap-3 items-center self-start text-2xl font-bold text-darkMode-text">
          <h1 className="text-darkMode-text">
            FAQ (Frequently Asked Questions)
          </h1>
        </div>
        <div className="mt-1 text-sm leading-none text-darkMode-subText">
          Get answers to common trading questions and sector-specific insights.
        </div>
      </div>

      {/* Content Section */}
      <div className="flex flex-col items-center mt-6 w-full max-md:max-w-full px-4 md:px-6 lg:px-8">
        {/* Category Tabs - Centering Container - MODIFIED */}
        <div className="flex justify-center w-full max-w-[737px]">
          {" "}
          {/* Removed md:max-w-full from here */}
          {/* Inner container to pass width to CategoryTabs */}
          <div className="w-full">
            {/* CategoryTabs component */}
            <CategoryTabs
              showFilterIcon={false}
              onFilterChange={handleFilterChange}
              categories={categories}
              defaultCategory={"all"}
            />
          </div>
        </div>

        {/* FAQ Items (remains the same) */}
        <div className="flex flex-col mt-8 max-w-full min-h-[500px] w-full md:w-[737px] bg-darkMode-layer1 rounded-xl p-4 md:p-6 shadow-md">
          {filteredFaqItems.length > 0 ? (
            filteredFaqItems.map((item, index) => (
              <FaqItem
                key={index}
                question={item.question}
                answer={item.answer}
                className={index > 0 ? "mt-4" : ""}
              />
            ))
          ) : (
            <div className="text-center text-darkMode-subText">
              No FAQs found for the selected category.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Market-teal themed version for landing pages with enhanced styling
const MarketingFaqContent = () => {
  const [filteredFaqItems, setFilteredFaqItems] = useState(allFaqItems);
  const [activeCategory, setActiveCategory] = useState("all");
  const [expandedItem, setExpandedItem] = useState(null);

  const handleFilterChange = (category) => {
    setActiveCategory(category);
    if (category === "all") {
      setFilteredFaqItems(allFaqItems);
    } else {
      setFilteredFaqItems(
        allFaqItems.filter((item) => item.category === category)
      );
    }
    setExpandedItem(null); // Reset expanded item when changing category
  };

  const toggleAccordion = (index) => {
    setExpandedItem(expandedItem === index ? null : index);
  };

  return (
    <div className="flex flex-col grow shrink max-md:max-w-full pt-20 px-6 bg-black bg-opacity-95 transition relative overflow-hidden min-h-screen justify-center">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-black via-black/95 to-black/90 pointer-events-none"></div>

      {/* Grid pattern overlay */}
      <div className="absolute inset-0 bg-grid-pattern bg-[size:20px_20px] pointer-events-none opacity-[0.03]"></div>

      {/* Animated teal glow effects */}
      <div className="absolute top-40 -left-20 w-96 h-96 bg-market-teal/15 rounded-full blur-[140px] pointer-events-none animate-pulse-slow"></div>
      <div className="absolute bottom-20 right-10 w-80 h-80 bg-market-teal/10 rounded-full blur-[120px] pointer-events-none animate-pulse-slower"></div>

      {/* Subtle animated lines/circles */}
      <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-market-teal/20 to-transparent"></div>
      <div className="absolute bottom-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-market-teal/20 to-transparent"></div>

      {/* Content with z-index to appear above the background effects */}
      <div className="relative z-10 w-full">
        {/* Header Section with enhanced styling */}
        <div className="flex flex-col w-full max-w-[1160px] max-md:max-w-full px-4 md:px-6 lg:px-8">
          <div className="flex gap-3 items-center self-start text-2xl font-bold text-white">
            <h1 className="text-white">
              <span className="text-market-teal drop-shadow-[0_0_8px_rgba(0,255,143,0.4)] font-extrabold">
                FAQ
              </span>{" "}
              <span className="text-gradient-white">
                (Frequently Asked Questions)
              </span>
            </h1>
          </div>
          <div className="mt-1 text-sm leading-none text-white/70">
            Get answers to common trading questions and sector-specific
            insights.
          </div>
        </div>

        {/* Content Section - exactly like FaqPage but with enhanced styling */}
        <div className="flex flex-col items-center mt-6 w-full max-md:max-w-full px-4 md:px-6 lg:px-8">
          {/* Category Tabs - with enhanced styling but same functionality */}
          <div className="flex justify-center w-full max-w-[737px] backdrop-blur-sm">
            <div className="w-full">
              {/* Custom tab styling wrapper */}
              <div className="bg-black/60 border border-market-teal/30 rounded-lg p-1 relative">
                {/* Animated sliding indicator */}
                <div
                  className="absolute h-[calc(100%-8px)] top-1 left-1 bg-market-teal/20 border border-market-teal/50 rounded-md transition-all duration-300 ease-in-out"
                  style={{
                    width: `calc(${100 / categories.length}% - 2px)`,
                    transform: `translateX(${categories.findIndex((c) => c.value === activeCategory) *
                      100
                      }%)`,
                  }}
                ></div>

                <div className="flex relative z-10">
                  {categories.map((category) => (
                    <button
                      key={category.value}
                      onClick={() => handleFilterChange(category.value)}
                      className={`flex-1 px-4 py-2 rounded-md transition-all duration-300 z-10 ${activeCategory === category.value
                        ? "text-market-teal font-medium"
                        : "text-white/70 hover:text-white hover:bg-white/5 border border-transparent"
                        }`}
                    >
                      {category.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* FAQ Items - scrollable with max 6 visible */}
          <div className="flex flex-col mt-8 max-w-full w-full md:w-[737px] max-h-[660px] overflow-y-auto custom-scrollbar bg-black/70 backdrop-blur-md border border-market-teal/30 rounded-xl p-4 md:p-6 shadow-[0_4px_30px_rgba(0,0,0,0.3),_0_0_2px_rgba(0,255,143,0.2)] transition-all hover:shadow-[0_8px_32px_rgba(0,0,0,0.4),_0_0_3px_rgba(0,255,143,0.3)]">
            {filteredFaqItems.length > 0 ? (
              filteredFaqItems.map((item, index) => (
                <div
                  key={index}
                  className={`${index > 0 ? "mt-6" : ""
                    } group border-b border-white/10 pb-4 transition-all hover:border-market-teal/30`}
                >
                  <div
                    className="flex justify-between items-center cursor-pointer"
                    onClick={() => toggleAccordion(index)}
                  >
                    <h3 className="text-market-teal font-medium group-hover:text-market-teal/90 transition-colors">
                      {item.question}
                    </h3>
                    <div
                      className={`text-market-teal transition-transform duration-300 ${expandedItem === index ? "rotate-180" : ""
                        }`}
                    >
                      <svg
                        xmlns="http://www.w3.org/2000/svg"
                        className="h-5 w-5"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </div>
                  </div>
                  <div
                    className={`mt-2 overflow-hidden transition-all duration-300 ease-in-out ${expandedItem === index
                      ? "max-h-96 opacity-100"
                      : "max-h-0 opacity-0"
                      }`}
                  >
                    <div className="text-white/80 group-hover:text-white/90 transition-colors pb-2">
                      {item.answer}
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="text-center text-white/70 py-8">
                No FAQs found for the selected category.
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Marketing version
export const MarketingFAQ = () => {
  return (
    <ThemeProvider variant="marketing">
      <MarketingFaqContent />
    </ThemeProvider>
  );
};

// Dashboard version - directly use the original FaqPage component
export const DashboardFAQ = () => {
  return <FaqPage />;
};

// Default export for the route
const FAQ = ({ variant = "marketing" }) => {
  return variant === "marketing" ? <MarketingFAQ /> : <DashboardFAQ />;
};

export default FAQ;
